<template>
  <v-card
    ref="card"
    class="c-card d-flex flex-column pa-3"
    :class="{ 'c-draft': isDraft }"
  >

    <!-- header -->
    <v-list-item class="c-header pa-0 mb-2" :class="{ 'c-expanded': expanded }" >
      <!-- media icon -->
      <v-list-item-avatar>
        <v-icon color="accent" x-large>
          {{ getMediaIcon(item.mediaType) }}
        </v-icon>
      </v-list-item-avatar>
      <!-- title -->
      <v-list-item-content class="c-titles pa-0">
        <v-card-title class="c-title pa-0 text-left">
          <ClampedText
            :max-lines="showSubtitle ? 3 : 4"
            @expanded="titleExpanded = $event"
          >
            {{ item.title }}
          </ClampedText>
        </v-card-title>
        <v-card-subtitle
          v-if="showSubtitle"
          class="c-subtitle pa-0 text-subtitle-1"
        >
          <ClampedText
            :max-lines="2"
            @expanded="subtitleExpanded = $event"
          >
            {{ item.subtitle }}
          </ClampedText>
        </v-card-subtitle>
      </v-list-item-content>
    </v-list-item>

    <!-- thumbnail -->
    <div class="c-thumbnail c-thumbnail-border mb-2">
      <v-img
        class="c-clickable ma-2"
        :class="{ 'c-thumbnail-cropped': isDocument || isJournal }"
        :aspect-ratio="thumbnailAspectRatio"
        :contain="item.contentTypeKeyword !== 'article' && item.contentTypeKeyword !== 'journal'"
        :src="thumbnail"
        @click="$emit('view:item')"
      />
      <v-img
        v-if="seriesThumbnail"
        class="c-thumbnail_series"
        :aspect-ratio="thumbnailAspectRatio"
        contain
        :src="seriesThumbnail"
        @click="$emit('view:item')"
      />
    </div>

    <div
      class="c-footer"
      :class="{ 'c-expanded': expanded, 'c-short': hideAbstract }"
    >

      <!-- topic -->
      <v-card-text class="c-topic accent--text pa-0 text-subtitle-1 text-left">
        <span>{{ isJournal ? topics : topic }}&nbsp;</span>
      </v-card-text>

      <!-- size and duration -->
      <v-card-text v-if="$actions.size" class="d-flex pa-0 text-subtitle-1">
        <div>
          {{ sizeInMB ? `${sizeInMB}MB` : '&nbsp;' }}
        </div>
        <v-spacer />
        <div>
          {{ durationInMinutes ? `${durationInMinutes} Min` : '&nbsp;' }}
        </div>
      </v-card-text>

      <!-- abstract -->
      <v-card-text v-if="showAbstract" class="c-abstract pa-0">
        <ClampedText
          :max-lines="3"
          @expanded="abstractExpanded = $event"
        >
          {{ item.abstract }}
        </ClampedText>
      </v-card-text>

    </div>

    <!-- action menu -->
    <ContentCardActions
      :item="item"
      @view:item="$emit('view:item')"
      @view:info="revealBack = true"
      @remove:favourite="$emit('remove:favourite')"
      @share="$emit('share')"
    />

    <!-- flip card -->
    <v-scroll-x-reverse-transition>
      <ContentCardBack v-model="revealBack" :item="item" @view:parent="$emit('view:parent')" />
    </v-scroll-x-reverse-transition>
  </v-card>
</template>

<script>
import ClampedText from '@/components/base/ClampedText'
import ContentCardActions from '@/components/content/ContentCardActions'
import ContentCardBack from '@/components/content/ContentCardBack'
import Players from '@/players/playerRegistry.json'
import { getImage } from '@/config/sanityConfig'

export default {
  name: 'ContentCard',

  components: {
    ClampedText,
    ContentCardActions,
    ContentCardBack
  },

  props: {
    item: {
      type: Object,
      required: true
    }
  },

  data: function () {
    return {
      thumbnailAspectRatio: 4 / 3,
      maxLines: 3,
      isFavourite: false,
      revealBack: false,
      showViewer: false,
      cardX: 0,
      cardY: 0,
      abstractExpanded: false,
      subtitleExpanded: false,
      titleExpanded: false
    }
  },

  computed: {
    /* context */

    locale() {
      return this.$store.state.i18nStore.locale
    },

    defaultLocale() {
      return this.$store.state.i18nStore.defaultLocale
    },

    favourite() {
      return this.$store.state.userStore.favourites.indexOf(this.item.id) > -1
    },

    /* metadata */

    durationInMinutes() {
      return this.item.durationInMinutes || ''
    },

    sizeInMB() {
      return this.item.sizeInMB || ''
    },

    topic() {
      return this.item.topic?.[this.locale] || this.item.topic?.[this.defaultLocale] || ''
    },

    topics() {
      return this.item.topics
        .map((topic) => topic[this.locale] || topic[this.defaultLocale] || '')
        .join(', ')
    },

    /* title & cover */

    isDocument() {
      return this.item.mediaType === 'article' || this.item.mediaType === 'pdf'
    },

    isJournal() {
      return this.item.mediaType === 'journal'
    },

    showSubtitle() {
      // const mediaList = ['journal']
      // return mediaList.includes(this.item.mediaType)
      return this.item.subtitle?.length > 0
    },

    hideAbstract() {
      return this.$actions.simple
    },

    showAbstract() {
      // const mediaList = ['journal']
      // return mediaList.includes(this.item.mediaType)
      return this.item.abstract?.length > 0
    },

    thumbnail() {
      // const url = this.item.cardImage?.url
      const image = this.item.cardImage?.obj
      const crop = image?.crop
      // const hotspot = image.hotspot

      return image
        ? crop
          ? getImage(image).width(640).height(480).url()
          : getImage(image).width(640).height(480).url()
        : ''
    },

    seriesThumbnail() {
      const series = this.item.series
      if (series) {
        const image = series.cardImage?.obj
        return getImage(image).width(100).height(75).url()
      }
      return ''
    },

    /* flex */

    expanded() {
      return this.titleExpanded || this.subtitleExpanded || this.abstractExpanded
    },

    isDraft() {
      return this.item.id.startsWith('drafts.')
    }
  },

  created: function () {
    this.isFavourite = this.favourite
  },

  mounted: function () {
    const cardRect = this.$refs.card.$el.getBoundingClientRect()
    this.cardX = Math.ceil(cardRect.left)
    this.cardY = Math.ceil(cardRect.top)
  },

  activated: function () {
    this.isFavourite = this.favourite
  },

  methods: {
    getCardHeight(colWidth) {
      // cardWidth
      const colGap = 12
      const rowGap = 12
      const cardWidth = Math.min(Math.max(colWidth - 2 * colGap, 275), 420)

      // title height
      const titlePadding = 2 * 12
      const titleHeight = 120 + titlePadding

      // thumbnail height
      const imageMarginWidth = 2 * 16 + 2 * 16
      const thumbnailHeight = (cardWidth - imageMarginWidth) * (3 / 4) + 2 * 16

      // fixed  blocks
      const topicHeight = 28
      const actionHeight = 60

      // totals
      const height = 2 * rowGap + titleHeight + thumbnailHeight + topicHeight + actionHeight
      return height
    },

    getMediaIcon(mediaType) {
      return Players.find((player) => player.mediaType === mediaType)?.icon || 'mdi-file'
    }
  }
}
</script>

<style lang="scss" scoped>
.c-card {
  position: relative;
  height: 100%;
  min-width: 275px;
  max-width: min(420px, 100%);
  border-radius: 16px;

  &.c-draft {
    opacity: 0.6;
    border: solid 8px var(--v-accent-base);
  }
}

.c-header {
  flex: 1 1 100%;
  /* max header is 130px; max footer is 100px */
  /* flex-basis: calc(50% * (130 / 100)); */

  &.c-expanded {
    min-height: unset;
  }
}
.c-footer {
  /* flex: 1 1 fit-content */
  /* max header is 130px; max footer is 100px */
  /* flex-basis: calc(50% * (100 / 130)); */
  width: 100%;
  min-height: 100px; /* abstract + subtitle = 72px + 28px */

  &.c-short {
    min-height: 32px;
  }

  &.c-expanded {
    min-height: unset;
  }
}

.c-subtitle {
  line-height: 1.2;
  margin-top: 0px; /* normally -16px */
}
.c-abstract {
  font-size: 1rem;
  line-height: 1.375;
}

.c-thumbnail {
  position: relative;
}
.c-thumbnail_series {
  position: absolute;
  bottom: 0px;
  right: 0px;
  width: 35%;
  border: solid 1px var(--v-primary-base);
}

.c-thumbnail-border {
  background-color: var(--v-background-base);
}

.c-thumbnail-cropped :deep(.v-image__image) {
  background-position: top center !important;
}
.c-topic {
  line-height: 1.3rem;
}
.c-clickable {
  cursor: pointer;
}
</style>
